import {IntlShape} from 'react-intl';

import type {PasswordValidationRules} from './types';

export enum MessageId {
    Required = 'required',
    Helper = 'helper',
    TooShort = 'too_short',
    Symbols = 'symbols',
    TooWeak = 'too_weak',
    Good = 'good',
    NoMatch = 'no_match',
    Match = 'match',
    General = 'general',
    TooLong = 'too_long',
    GoodForOne = 'good_for_one'
}

export const getMessageHeader = (id: MessageId, intl: IntlShape) => {
    switch (id) {
    case MessageId.Required:
        return intl.formatMessage({
            id: 'new_password.message.header.required',
            defaultMessage: 'Choose a secure password',
        });
    case MessageId.Symbols:
        return intl.formatMessage({
            id: 'new_password.message.header.symbols',
            defaultMessage: "This password doesn't meet the requirements",
        });
    case MessageId.TooShort:
        return intl.formatMessage({
            id: 'new_password.message.header.too_short',
            defaultMessage: 'Password is too short',
        });
    case MessageId.TooLong:
        return intl.formatMessage({
            id: 'new_password.message.header.too_long',
            defaultMessage: 'Password is too long',
        });
    case MessageId.TooWeak:
        return intl.formatMessage({
            id: 'new_password.message.header.too_weak',
            defaultMessage: 'Password is too simple',
        });
    case MessageId.NoMatch:
        return intl.formatMessage({
            id: 'new_password.message.header.no_match',
            defaultMessage: 'Password mismatch',
        });
    case MessageId.Helper:
        return intl.formatMessage({
            id: 'new_password.message.header.helper',
            defaultMessage: 'Choose a secure password',
        });
    case MessageId.Good:
    case MessageId.GoodForOne:
        return intl.formatMessage({id: 'new_password.message.header.good', defaultMessage: 'Great password'});
    case MessageId.Match:
        return intl.formatMessage({id: 'new_password.message.header.match', defaultMessage: 'Passwords match'});
    default:
        return intl.formatMessage({
            id: 'new_password.message.header.general',
            defaultMessage: 'Something went wrong',
        });
    }
};

export const getPasswordWeakDescription = (validationsRules: PasswordValidationRules, intl: IntlShape, useAdd = false) => {
    let beginWith = intl.formatMessage({id: 'new_password.message.too_weak.use', defaultMessage: 'Use'});
    if (useAdd) {
        beginWith = intl.formatMessage({id: 'new_password.message.too_weak.add', defaultMessage: 'Add'});
    }
    /* eslint-disable quote-props */
    const sentences: Record<string, string> = {
        '0001': intl.formatMessage({id: 'new_password.message.too_weak.0001', defaultMessage: 'symbols'}),
        '0010': intl.formatMessage({id: 'new_password.message.too_weak.0010', defaultMessage: 'numbers'}),
        '0011': intl.formatMessage({id: 'new_password.message.too_weak.0011', defaultMessage: 'numbers and symbols'}),
        '0100': intl.formatMessage({id: 'new_password.message.too_weak.0100', defaultMessage: 'capital letters'}),
        '0101': intl.formatMessage({id: 'new_password.message.too_weak.0101', defaultMessage: 'capital letters and symbols'}),
        '0110': intl.formatMessage({id: 'new_password.message.too_weak.0110', defaultMessage: 'capital letters and numbers'}),
        '0111': intl.formatMessage({id: 'new_password.message.too_weak.0111', defaultMessage: 'capital letters, numbers and symbols'}),
        '1000': intl.formatMessage({id: 'new_password.message.too_weak.1000', defaultMessage: 'small letters'}),
        '1001': intl.formatMessage({id: 'new_password.message.too_weak.1001', defaultMessage: 'small letters and symbols'}),
        '1010': intl.formatMessage({id: 'new_password.message.too_weak.1010', defaultMessage: 'small letters and numbers'}),
        '1011': intl.formatMessage({id: 'new_password.message.too_weak.1011', defaultMessage: 'small letters, numbers and symbols'}),
        '1100': intl.formatMessage({id: 'new_password.message.too_weak.1100', defaultMessage: 'small and capital letters'}),
        '1101': intl.formatMessage({id: 'new_password.message.too_weak.1101', defaultMessage: 'small, capital letters and symbols'}),
        '1110': intl.formatMessage({id: 'new_password.message.too_weak.1110', defaultMessage: 'small, capital letters and numbers'}),
        '1111': intl.formatMessage({id: 'new_password.message.too_weak.1111', defaultMessage: 'small and capital letters, numbers and symbols'}),
    };
    /* eslint-enable quote-props */

    const complexityRules = `${Number(validationsRules.PasswordRequireLowercase)}${Number(validationsRules.PasswordRequireUppercase)}${Number(validationsRules.PasswordRequireNumber)}${Number(validationsRules.PasswordRequireSymbol)}`;

    if (complexityRules === '0000') {
        return intl.formatMessage(
            {
                id: 'new_password.message.description.required_or_helper',
                defaultMessage: 'We recommend a password of {length} characters or more',
            }, {length: validationsRules.PasswordMinimumLength},
        );
    }

    return beginWith + ' ' + sentences[complexityRules];
};

const getNoMatchDescription = (password: string, repeatPassword: string, intl: IntlShape) => {
    if (password.length === repeatPassword.length) {
        return intl.formatMessage({
            id: 'new_password.message.description.no_match.typo',
            defaultMessage: 'Perhaps you made a typo',
        });
    }

    return intl.formatMessage({
        id: 'new_password.message.description.no_match.length',
        defaultMessage: 'Check the number of characters',
    });
};

export const getMessageDescription = (
    id: MessageId,
    password: string,
    repeatPassword: string,
    validationsRules: PasswordValidationRules,
    intl: IntlShape,
) => {
    switch (id) {
    case MessageId.Required:
    case MessageId.Helper:
        return intl.formatMessage(
            {
                id: 'new_password.message.description.required_or_helper',
                defaultMessage: 'We recommend a password of {length} characters or more',
            },
            {length: validationsRules.PasswordMinimumLength},
        );
    case MessageId.Symbols:
        return intl.formatMessage({
            id: 'new_password.message.description.symbols',
            defaultMessage: 'Use latin (a-z), numbers and symbols without spaces',
        });
    case MessageId.TooShort:
        return getPasswordWeakDescription(validationsRules, intl, true);
    case MessageId.TooLong:
        return intl.formatMessage({
            id: 'new_password.message.description.too_long',
            defaultMessage: 'It must not exceed {length} characters',
        }, {length: validationsRules.PasswordMaximumLength});
    case MessageId.TooWeak:
        return getPasswordWeakDescription(validationsRules, intl);
    case MessageId.NoMatch:
        return getNoMatchDescription(password, repeatPassword, intl);
    case MessageId.Good:
        return intl.formatMessage({
            id: 'new_password.message.description.good',
            defaultMessage: 'Repeat to test yourself',
        });
    case MessageId.Match:
    case MessageId.GoodForOne:
        return intl.formatMessage({
            id: 'new_password.message.description.match',
            defaultMessage: 'Keep it in a safe place',
        });
    default:
        return intl.formatMessage({
            id: 'new_password.message.description.general',
            defaultMessage: 'Try again later',
        });
    }
};
